import { Link } from "react-router-dom";
import { useSession } from "../state/session.js";
import { useCallLogs, useContacts, useTick } from "../hooks.js";
import { clearAllConversations } from "../storage/db.js";
import { timeLeft } from "../storage/sweeper.js";
import { NotificationsPrompt } from "./NotificationsPrompt.js";
import { usePresenceStore } from "../state/presence.js";

function fmtLeft(ms: number): string {
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, "0")} left`;
}

export function Home() {
  const account = useSession((s) => s.account);
  const status = useSession((s) => s.channelStatus);
  const lock = useSession((s) => s.lock);
  const contacts = useContacts();
  const callLogs = useCallLogs();
  const now = useTick();
  const peers = usePresenceStore((s) => s.peers);
  const usernameFor = (peerUserId: string) =>
    contacts.find((c) => c.peerUserId === peerUserId)?.username ?? peerUserId.slice(0, 8);

  async function clearAll() {
    if (!confirm("Permanently clear every chat and call log? Contacts stay — this can't be undone.")) return;
    await clearAllConversations();
  }

  return (
    <>
      <div className="topbar">
        <span className={`dot ${status === "online" ? "online" : status === "offline" ? "offline" : ""}`} />
        <strong className="grow">{account?.username}</strong>
        <Link to="/add">
          <button className="topbar-icon-btn" title="Add contact" aria-label="Add contact">➕</button>
        </Link>
        <button className="topbar-icon-btn" onClick={() => void clearAll()} title="Clear all" aria-label="Clear all">🧹</button>
        <Link to="/settings">
          <button className="topbar-icon-btn" title="Settings" aria-label="Settings">⚙️</button>
        </Link>
        <button className="topbar-icon-btn" onClick={lock} title="Lock" aria-label="Lock">🔒</button>
      </div>

      <NotificationsPrompt />

      {contacts.length === 0 ? (
        <div className="center">
          <div className="card">
            <h1>No conversations yet</h1>
            <p>Share your invite link or scan someone else's to start a conversation.</p>
            <Link to="/add"><button className="primary">Add a contact</button></Link>
          </div>
        </div>
      ) : (
        <div className="list">
          {contacts.map((c) => (
            <Link className="row sensitive" key={c.peerUserId} to={`/chat/${c.peerUserId}`}>
              <span className="avatar">
                {c.username.slice(0, 1).toUpperCase()}
                {peers[c.peerUserId]?.online && <span className="avatar-online" aria-label="online" />}
              </span>
              <span>
                <div>{c.username}</div>
                <div className="sub">
                  {peers[c.peerUserId]?.online ? "online" : c.verified ? "verified" : "not verified"}
                </div>
              </span>
            </Link>
          ))}
        </div>
      )}

      {callLogs.length > 0 && (
        <div className="list">
          <div className="row" style={{ borderBottom: "none", paddingBottom: 4 }}>
            <span className="sub">Recent calls</span>
          </div>
          {callLogs.map((log) => (
            <Link className="row sensitive" key={log.id} to={`/chat/${log.peerUserId}`}>
              <span className="avatar">{usernameFor(log.peerUserId).slice(0, 1).toUpperCase()}</span>
              <span>
                <div>{usernameFor(log.peerUserId)}</div>
                <div className="sub">{log.dir === "out" ? "Outgoing" : "Incoming"} · {log.outcome}</div>
              </span>
              <span className="grow" />
              <span className="sub">{fmtLeft(timeLeft(log.expiresAt, now) ?? 0)}</span>
            </Link>
          ))}
        </div>
      )}

    </>
  );
}
